import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './cn';

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & {
  busy?: boolean;
  variant?: 'primary' | 'secondary' | 'quiet';
}>(function Button({ busy = false, variant = 'primary', className, disabled, children, ...props }, ref) {
  return (
    <button ref={ref} disabled={disabled || busy} aria-busy={busy || undefined} className={cn('ui-button', `ui-button--${variant}`, className)} {...props}>
      {busy ? <span className="ui-spinner" aria-hidden="true" /> : null}
      <span>{children}</span>
    </button>
  );
});

export function Field({ label, error, hint, required, children }: {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactElement;
}) {
  const generatedId = useId();
  const child = Children.only(children) as ReactElement<Record<string, unknown>>;
  const childId = typeof child.props.id === 'string' ? child.props.id : `field-${generatedId}`;
  const descriptionId = error || hint ? `${childId}-description` : undefined;
  return (
    <div className="ui-field">
      <label htmlFor={childId} className="ui-label" data-required={required || undefined}>{label}</label>
      {isValidElement(child) ? cloneElement(child, {
        id: childId,
        required: required || Boolean(child.props.required),
        'aria-invalid': error ? true : child.props['aria-invalid'],
        /*
         * Merged, not overwritten.
         *
         * This assigned `descriptionId` unconditionally, so a caller that had
         * already pointed the control at its own help text lost it — and when
         * the Field had no `error` or `hint`, `descriptionId` is `undefined`,
         * so the caller's value was replaced with nothing at all.
         *
         * Confirmed on deployed staging: the claim lookup's order-number input
         * sets aria-describedby="claim-lookup-hint", that paragraph is in the
         * DOM, and the rendered input carried no aria-describedby whatsoever.
         * The hint explaining how to find an order number was invisible to a
         * screen reader on the one screen where getting the number wrong is the
         * most common way to fail.
         */
        'aria-describedby':
          [descriptionId, child.props['aria-describedby']].filter(Boolean).join(' ') || undefined,
      }) : child}
      {error || hint ? <p id={descriptionId} className={cn('ui-field-help', error && 'ui-field-error')}>{error ?? hint}</p> : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cn('ui-control', className)} {...props} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, ...props }, ref) {
  return <select ref={ref} className={cn('ui-control', className)} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cn('ui-control ui-textarea', className)} {...props} />;
});

export const Notice = forwardRef<HTMLDivElement, { tone?: 'info' | 'success' | 'warning' | 'error'; children: ReactNode; className?: string }>(function Notice({ tone = 'info', children, className }, ref) {
  return <div ref={ref} role={tone === 'error' ? 'alert' : 'status'} aria-live={tone === 'error' ? 'assertive' : 'polite'} tabIndex={-1} className={cn('ui-notice', `ui-notice--${tone}`, className)}>{children}</div>;
});

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cn('ui-card', className)}>{children}</section>;
}
